import React, { useEffect, useState } from "react";
import { PencilSimple } from "@phosphor-icons/react";
import OTPInput from "components/auth/OTPInput";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import CircularSpinner from "components/common/loaders/CircularSpinner";
import { useLocale } from "lib/hooks/useLocale";
import { OTPChannel, OTPVerificationDetails } from "lib/types/user";
import { OTPDeliveryType, sendOtpWithPhoneNumber, verifyOtp, resentOtp } from "lib/utils/auth";
import { errorToast } from "lib/utils/toasters";
import { isValidEmail } from "lib/utils/validations";

interface NoEmailFoundProps {
  merchantId: string;
  userId?: string;
  captchaProvider?: "TURNSTILE" | "ALTCHA";
  onVerified: (email: string) => Promise<void>;
}

const maskEmail = (email: string): string => {
  const atIndex = email.lastIndexOf("@");
  if (atIndex === -1) return email;
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex);
  const visibleStart = Math.min(3, Math.floor(local.length / 2));
  const visibleEnd = Math.min(2, local.length - visibleStart);
  const masked =
    local.slice(0, visibleStart) + "***" + (visibleEnd > 0 ? local.slice(local.length - visibleEnd) : "");
  return `${masked}${domain}`;
};

const NoEmailFound: React.FC<NoEmailFoundProps> = ({
  merchantId,
  userId,
  captchaProvider = "TURNSTILE",
  onVerified,
}) => {
  const { t } = useLocale();
  const [screen, setScreen] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [contextId, setContextId] = useState("");
  const [otp, setOtp] = useState("");
  const [hasOtpError, setHasOtpError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [counter, setCounter] = useState(-1);
  const [counterExpired, setCounterExpired] = useState(false);

  useEffect(() => {
    if (counter === 0) setCounterExpired(true);
    const timer: ReturnType<typeof setInterval> | false =
      counter > 0 && setInterval(() => setCounter((c) => c - 1), 1000);
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [counter]);

  const handleSendOtp = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError(t("shopify_login.email_required"));
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailError(t("shopify_login.invalid_email"));
      return;
    }

    setIsLoading(true);
    try {
      const verificationDetails: OTPVerificationDetails = {
        identifier: trimmed,
        merchantId,
        userId,
        forceVerification: true,
      };
      const contextMetadata: OTPDeliveryType = { context: "AUTH", requestContext: "LOGIN" };
      const result = await sendOtpWithPhoneNumber(verificationDetails, contextMetadata, "", captchaProvider);

      if (result?.context_id) {
        setContextId(result.context_id);
        setCounter(30);
        setCounterExpired(false);
        setScreen("otp");
      } else {
        errorToast(t("shopify_login.send_otp_failed"));
      }
    } catch (error) {
      console.error(error);
      errorToast(t("shopify_login.send_otp_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 4 || isLoading) return;
    setIsLoading(true);
    try {
      await verifyOtp(contextId, otp, true);
      await onVerified(email.trim());
    } catch {
      setHasOtpError(true);
      setOtp("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtp("");
    setHasOtpError(false);
    try {
      await resentOtp(contextId, "EMAIL" as OTPChannel);
      setCounter(30);
      setCounterExpired(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleBackToEmail = () => {
    setScreen("email");
    setOtp("");
    setHasOtpError(false);
  };

  useEffect(() => {
    if (otp.length === 4) {
      handleVerifyOtp();
    }
  }, [otp]);

  if (screen === "otp") {
    return (
      <div className="flex w-full flex-col">
        {/* Header */}
        <div className="flex flex-col items-start px-6 pb-3 pt-6">
          <div className="flex flex-col gap-0.5">
            <span className="text-text-md-semibold text-zinc-800">
              {t("shopify_login.almost_there")}
            </span>
            <span className="text-text-sm-regular text-zinc-500">
              {t("shopify_login.verify_email_address")}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 bg-gray-lightest p-4">
          <div className="flex w-full flex-col gap-4">
            {/* Masked email with edit icon */}
            <div className="flex items-center gap-1.5">
              <p className="text-text-sm-medium text-zinc-500">
                {t("shopify_login.otp_sent_to")}{" "}
                <span className="text-text-sm-semibold text-zinc-800">{maskEmail(email.trim())}</span>
              </p>
              <button onClick={handleBackToEmail} className="flex shrink-0 items-center justify-center">
                <PencilSimple className="h-4 w-4 text-zinc-500" weight="regular" />
              </button>
            </div>

            <OTPInput
              OTPLength={4}
              disabled={isLoading}
              autoFocus={true}
              hasError={hasOtpError}
              value={otp}
              disableTheme={true}
              onChange={(value) => {
                setOtp(value);
                if (hasOtpError) setHasOtpError(false);
              }}
              inputClassName="!rounded-[10px] !h-11 !text-base !border"
            />

            {hasOtpError && (
              <p className="text-center text-text-xs-medium text-ouch">{t("shopify_login.incorrect_otp")}</p>
            )}

            <div className="flex items-start gap-1">
              <span className="text-text-sm-medium text-zinc-800 opacity-70">
                {t("shopify_login.not_received")}
              </span>
              {counterExpired || counter <= 0 ? (
                <button
                  onClick={handleResendOtp}
                  disabled={isLoading}
                  className="flex items-center gap-1 text-text-sm-semibold text-zinc-800 disabled:opacity-50">
                  {isLoading && <CircularSpinner height="h-3" width="w-3" color="text-zinc-800" />}
                  {t("resend_otp")}
                </button>
              ) : (
                <span className="text-text-sm-semibold text-zinc-800 opacity-50">
                  {t("shopify_login.resend_in", { counter })}
                </span>
              )}
            </div>
          </div>

          <PrimaryButton
            buttonText={t("continue")}
            height="h-11"
            borderRadius="rounded-xl"
            isLoading={isLoading}
            isDisabled={isLoading || otp.length !== 4}
            onClick={handleVerifyOtp}
            className="!bg-[#27272a] !text-white"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col">
      {/* Header */}
      <div className="flex flex-col items-start px-6 pb-3 pt-6">
        <div className="flex flex-col gap-0.5">
          <span className="text-text-md-semibold text-zinc-800">
            {t("shopify_login.almost_there")}
          </span>
          <span className="text-text-sm-regular text-zinc-500">
            {t("shopify_login.add_email_subtitle")}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-3 bg-gray-lightest p-4">
        <div className="flex flex-col gap-1.5">
          <label className="px-1 text-text-xs-medium text-zinc-500">
            {t("shopify_login.email_label")}
          </label>

          <div className="rounded-xl border border-[rgba(9,9,11,0.06)] bg-white px-3 py-2">
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSendOtp();
              }}
              placeholder={t("shopify_login.email_placeholder")}
              className="h-[38px] w-full bg-transparent text-text-sm-medium text-zinc-800 outline-none placeholder:text-zinc-500"
            />
          </div>

          {emailError && <p className="px-1 text-text-xs-medium text-ouch">{emailError}</p>}
        </div>

        <PrimaryButton
          buttonText={t("continue")}
          height="h-11"
          borderRadius="rounded-xl"
          isLoading={isLoading}
          isDisabled={isLoading || !email.trim()}
          onClick={handleSendOtp}
          className="!bg-[#27272a] !text-white"
        />
      </div>
    </div>
  );
};

export default NoEmailFound;
